import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Box, TextField, Paper, Typography, List, ListItem, ListItemText,
  Chip, CircularProgress, InputAdornment, Popper, ClickAwayListener, Divider
} from '@mui/material';
import { Search, Inventory2, Person, Computer, Apps, AccountTree, MenuBook, BugReport, Storefront, Description, Work, ConfirmationNumber } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';

const MODULE_ICONS = {
  Asset: <Inventory2 fontSize="small" />,
  User: <Person fontSize="small" />,
  'Hardware Request': <Computer fontSize="small" />,
  Software: <Apps fontSize="small" />,
  'Configuration Item': <AccountTree fontSize="small" />,
  'Knowledge Article': <MenuBook fontSize="small" />,
  Problem: <BugReport fontSize="small" />,
  Vendor: <Storefront fontSize="small" />,
  Contract: <Description fontSize="small" />,
  Project: <Work fontSize="small" />,
  Ticket: <ConfirmationNumber fontSize="small" />,
};

export default function GlobalSearch() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const debounceRef = useRef(null);
  const navigate = useNavigate();

  const search = useCallback(async (q) => {
    if (q.length < 2) { setResults(null); setOpen(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get('/search', { params: { q } });
      setResults(data);
      setOpen(data.totalResults > 0);
    } catch { setResults(null); }
    finally { setLoading(false); }
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(val), 300);
  };

  const handleSelect = (item) => {
    setOpen(false);
    setQuery('');
    navigate(item.link);
  };

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  // Ctrl+K / Cmd+K shortcut to focus search
  useEffect(() => {
    const handleGlobalKeyDown = (event) => {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const modifier = isMac ? event.metaKey : event.ctrlKey;
      if (modifier && event.key === 'k') {
        event.preventDefault();
        const input = anchorRef.current?.querySelector('input');
        if (input) {
          input.focus();
          input.select();
        }
      }
    };
    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, []);

  const allItems = results ? Object.values(results.results).flat() : [];

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box sx={{ position: 'relative', width: { xs: 200, sm: 320, md: 400 } }} ref={anchorRef}>
        <TextField
          size="small"
          placeholder="Search everything... (Ctrl+K)"
          value={query}
          onChange={handleChange}
          onFocus={() => { if (allItems.length > 0) setOpen(true); }}
          fullWidth
          InputProps={{
            startAdornment: <InputAdornment position="start"><Search fontSize="small" color="action" /></InputAdornment>,
            endAdornment: loading ? <CircularProgress size={16} /> : null,
            sx: { borderRadius: 2, bgcolor: 'action.hover', fontSize: '0.875rem' }
          }}
        />
        <Popper open={open} anchorEl={anchorRef.current} placement="bottom-start" sx={{ zIndex: 1300, width: anchorRef.current?.clientWidth || 400 }}>
          <Paper elevation={8} sx={{ mt: 0.5, maxHeight: 400, overflow: 'auto', borderRadius: 2 }}>
            {allItems.length === 0 && !loading && (
              <Box sx={{ p: 2 }}><Typography variant="body2" color="text.secondary">No results found</Typography></Box>
            )}
            <List dense disablePadding>
              {allItems.map((item, i) => (
                <React.Fragment key={`${item.type}-${item._id}`}>
                  <ListItem button onClick={() => handleSelect(item)} sx={{ px: 2, py: 1 }}>
                    <Box sx={{ mr: 1.5, color: 'primary.main' }}>{MODULE_ICONS[item.type] || <Search fontSize="small" />}</Box>
                    <ListItemText
                      primary={<Typography variant="body2" fontWeight={600} noWrap>{item.title}</Typography>}
                      secondary={<Typography variant="caption" color="text.secondary" noWrap>{item.subtitle}</Typography>}
                    />
                    <Chip label={item.type} size="small" variant="outlined" sx={{ fontSize: '0.6875rem', height: 22, flexShrink: 0 }} />
                  </ListItem>
                  {i < allItems.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
            {results && (
              <Box sx={{ p: 1, borderTop: 1, borderColor: 'divider', bgcolor: 'action.hover' }}>
                <Typography variant="caption" color="text.secondary">{results.totalResults} result(s) found</Typography>
              </Box>
            )}
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
}
